import { Fragment, useEffect, useRef, useState } from "react";
import type { JSX } from "react";
import { Button, Flex, Message, Text } from "@aws-amplify/ui-react";
import type {
  LocationData,
  StorageBrowserType,
  UseView,
} from "@aws-amplify/ui-react-storage/browser";

import { folderDisplayName, projectDisplayName } from "../folderNames";
import { ListingLayout } from "./layout";
import { LISTING_PAGE_SIZE } from "./pagination";
import type { ProjectSelection } from "./projectSelection";
import type { ToolbarIconName } from "./toolbar";
import { ToolbarIconButton, ToolbarLabelledButton } from "./toolbar";
import { AppRole } from "@/auth/roles";
import type { LocationBreadcrumbSource } from "./folderTypes";
import {
  FolderTypes,
  folderType,
  isDeleteAvailable,
  isReviewAvailable,
  isStaffWritableFolder,
  splitSegments,
  uploadRulesFor,
} from "./folderTypes";
import { UploadDropZone } from "../upload/dropZone";
import { useUploadFilePicker } from "../upload/filePicker";
import { rejectedFilesSummary, uploadLimitsLabel } from "../upload/messages";
import type { UploadRules } from "../upload/validation";
import {
  isAcceptableUploadFile,
  uploadAcceptAttribute,
} from "../upload/validation";

const UPLOAD_ACTION_TYPE = "upload";
const DOWNLOAD_ACTION_TYPE = "download";
const DELETE_ACTION_TYPE = "delete";
const APPROVE_ACTION_TYPE = "approve";
const REJECT_ACTION_TYPE = "reject";
const BREADCRUMB_SEPARATOR = ">";
/** The root and organization a customer's location prefix opens with, and never sees. */
const CUSTOMER_HIDDEN_SEGMENT_COUNT = 2;
const ORGANIZATION_SEGMENT_INDEX = 1;
const PROJECT_SEGMENT_INDEX = 2;
// The search issues a fresh S3 list request, so typing is allowed to settle first.
const SEARCH_DEBOUNCE_MILLISECONDS = 300;

type EmptyStateContent = {
  readonly title: string;
  readonly description: string;
};

const NO_RESULTS_CONTENT: EmptyStateContent = {
  title: "No results yet",
  description: "Files will appear here once processing is complete",
};

const NO_SHARED_FILES_CONTENT: EmptyStateContent = {
  title: "Nothing shared yet",
  description: "Various files Evido shares with you will appear here",
};

const NO_UPLOADS_CONTENT: EmptyStateContent = {
  title: "No files uploaded",
  description: "Uploaded files will appear here",
};

const NOTHING_FOR_APPROVAL_CONTENT: EmptyStateContent = {
  title: "Nothing awaiting approval",
  description: "Files uploaded for review will appear here",
};

/** The catch-all, so that no folder ever renders as a blank panel. */
const EMPTY_FOLDER_CONTENT: EmptyStateContent = {
  title: "Nothing here yet",
  description: "This folder is empty",
};

const EMPTY_STATE_CONTENT: Record<FolderTypes, EmptyStateContent> = {
  [FolderTypes.Upload]: NO_UPLOADS_CONTENT,
  [FolderTypes.ForApproval]: NOTHING_FOR_APPROVAL_CONTENT,
  [FolderTypes.Results]: NO_RESULTS_CONTENT,
  [FolderTypes.SharedByEvido]: NO_SHARED_FILES_CONTENT,
  [FolderTypes.StaffDocuments]: EMPTY_FOLDER_CONTENT,
  [FolderTypes.Other]: EMPTY_FOLDER_CONTENT,
};

const NO_MATCHES_CONTENT: EmptyStateContent = {
  title: "No matches",
  description: "No files in this folder match your search",
};

const SEARCH_SCAN_LIMIT = 10000;

const NO_MATCHES_TRUNCATED_CONTENT: EmptyStateContent = {
  title: "No matches",
  description: `Only the first ${SEARCH_SCAN_LIMIT.toLocaleString(
    "en-US",
  )} files in this folder were searched`,
};

const LISTING_LIMIT_LABEL = LISTING_PAGE_SIZE.toLocaleString("en-US");

/** Shown in place of a second page, which would be sorted apart from the first. */
const TRUNCATED_FOLDER_MESSAGE = `This folder holds more than ${LISTING_LIMIT_LABEL} files. Only the first ${LISTING_LIMIT_LABEL}, by name, are shown and sorted.`;

export interface Breadcrumb {
  readonly label: string;
  /** Omitted for the current folder and for segments that cannot be addressed. */
  readonly onNavigate?: () => void;
}

const LAST_MODIFIED_DATE_FORMAT = new Intl.DateTimeFormat("en-US", {
  day: "numeric",
  month: "short",
  year: "numeric",
});
const LAST_MODIFIED_TIME_FORMAT = new Intl.DateTimeFormat("en-US", {
  hour: "numeric",
  minute: "2-digit",
});

export function formatLastModified(lastModified: Date): string {
  const day = LAST_MODIFIED_DATE_FORMAT.format(lastModified);
  const time = LAST_MODIFIED_TIME_FORMAT.format(lastModified);
  return `${day} at ${time}`;
}

function segmentLabel(
  segments: readonly string[],
  index: number,
  projectPrefixRoot: string,
): string {
  const segment = segments[index] ?? "";
  const isNamedSegment =
    (index === ORGANIZATION_SEGMENT_INDEX || index === PROJECT_SEGMENT_INDEX) &&
    segments[0] === projectPrefixRoot;
  return isNamedSegment
    ? projectDisplayName(segment)
    : folderDisplayName(segment, projectPrefixRoot);
}

function firstVisibleSegmentIndex(
  prefixSegments: readonly string[],
  projectPrefixRoot: string,
): number {
  return prefixSegments[0] === projectPrefixRoot &&
    prefixSegments.length > CUSTOMER_HIDDEN_SEGMENT_COUNT
    ? CUSTOMER_HIDDEN_SEGMENT_COUNT
    : 0;
}

function folderCrumbLabels(
  location: LocationBreadcrumbSource,
  projectPrefixRoot: string,
): string[] {
  const { current } = location;
  if (!current) {
    return [];
  }

  const prefixSegments = splitSegments(current.prefix);
  const segments = [...prefixSegments, ...splitSegments(location.path)];
  const firstVisibleIndex = firstVisibleSegmentIndex(
    prefixSegments,
    projectPrefixRoot,
  );

  return segments
    .slice(firstVisibleIndex)
    .map((_segment, offset) =>
      segmentLabel(segments, offset + firstVisibleIndex, projectPrefixRoot),
    );
}

export function breadcrumbTrail(
  location: LocationBreadcrumbSource,
  projectPrefixRoot: string,
): Breadcrumb[] {
  return folderCrumbLabels(location, projectPrefixRoot).map((label) => ({
    label,
  }));
}

function segmentNavigation(
  index: number,
  prefixDepth: number,
  pathSegments: readonly string[],
  current: LocationData,
  onNavigate: (location: LocationData, path?: string) => void,
): (() => void) | undefined {
  if (index < prefixDepth - 1) {
    return undefined;
  }
  if (index === prefixDepth - 1) {
    return () => {
      onNavigate(current, "");
    };
  }
  const subPath = `${pathSegments.slice(0, index - prefixDepth + 1).join("/")}/`;
  return () => {
    onNavigate(current, subPath);
  };
}

export function buildBreadcrumbs(
  location: LocationBreadcrumbSource,
  onNavigateHome: () => void,
  onNavigate: (location: LocationData, path?: string) => void,
  projectPrefixRoot: string,
  /** Set for a customer with several projects: leads their organization crumb back to the list. */
  onNavigateToProjects?: () => void,
): Breadcrumb[] {
  const { current } = location;
  if (!current) {
    return [];
  }

  const prefixSegments = splitSegments(current.prefix);
  const pathSegments = splitSegments(location.path);
  const firstVisibleIndex = firstVisibleSegmentIndex(
    prefixSegments,
    projectPrefixRoot,
  );
  const lastIndex = prefixSegments.length + pathSegments.length - 1;

  const crumbs = folderCrumbLabels(location, projectPrefixRoot).map(
    (label, offset): Breadcrumb => {
      const index = offset + firstVisibleIndex;
      // A user's leading project crumb is no folder they can open, so it links home.
      if (offset === 0 && firstVisibleIndex > 0) {
        return { label, onNavigate: onNavigateHome };
      }
      return {
        label,
        onNavigate:
          index === lastIndex
            ? undefined
            : segmentNavigation(
                index,
                prefixSegments.length,
                pathSegments,
                current,
                onNavigate,
              ),
      };
    },
  );
  if (!onNavigateToProjects || firstVisibleIndex === 0) {
    return crumbs;
  }
  const organizationCrumb: Breadcrumb = {
    label: projectDisplayName(prefixSegments[ORGANIZATION_SEGMENT_INDEX] ?? ""),
    onNavigate: onNavigateToProjects,
  };
  return [organizationCrumb, ...crumbs];
}

function useSearchAsTyped(
  searchQuery: string,
  locationKey: string,
  onSearch: () => void,
  onSearchClear: () => void,
): void {
  const handlers = useRef({ onSearch, onSearchClear });
  useEffect(() => {
    handlers.current = { onSearch, onSearchClear };
  });

  const searched = useRef({ locationKey, hasRun: false });

  useEffect(() => {
    if (searched.current.locationKey !== locationKey) {
      searched.current = { locationKey, hasRun: false };
      return undefined;
    }

    if (searchQuery === "") {
      if (searched.current.hasRun) {
        searched.current.hasRun = false;
        handlers.current.onSearchClear();
      }
      return undefined;
    }

    const timer = setTimeout(() => {
      searched.current.hasRun = true;
      handlers.current.onSearch();
    }, SEARCH_DEBOUNCE_MILLISECONDS);

    return () => {
      clearTimeout(timer);
    };
  }, [locationKey, searchQuery]);
}

function emptyStateContent(
  location: LocationBreadcrumbSource,
  searchQuery: string,
  hasExhaustedSearch: boolean,
): EmptyStateContent {
  if (searchQuery !== "") {
    return hasExhaustedSearch
      ? NO_MATCHES_TRUNCATED_CONTENT
      : NO_MATCHES_CONTENT;
  }
  return EMPTY_STATE_CONTENT[folderType(location)];
}

function EmptyStateCard({
  content,
}: {
  content: EmptyStateContent;
}): JSX.Element {
  return (
    <Flex alignItems="center" flex="1" justifyContent="center" padding="1.5rem">
      <Flex
        alignItems="center"
        backgroundColor="background.primary"
        borderColor="border.primary"
        borderRadius="large"
        borderStyle="solid"
        borderWidth="1px"
        color="font.primary"
        direction="column"
        gap="0.75rem"
        maxWidth="24rem"
        padding="2.5rem 2rem"
      >
        <svg
          aria-hidden="true"
          fill="none"
          height="24"
          viewBox="0 0 24 24"
          width="24"
          xmlns="http://www.w3.org/2000/svg"
        >
          <circle
            cx="12"
            cy="12"
            r="9"
            stroke="currentColor"
            strokeWidth="1.5"
          />
          <path
            d="M12 11v5.5"
            stroke="currentColor"
            strokeLinecap="round"
            strokeWidth="1.5"
          />
          <circle cx="12" cy="7.75" fill="currentColor" r="1" />
        </svg>
        <Text fontSize="13px" fontWeight="600">
          {content.title}
        </Text>
        <Text color="font.secondary" fontSize="14px" textAlign="center">
          {content.description}
        </Text>
      </Flex>
    </Flex>
  );
}

/**
 * Render a folder trail, turning every crumb that can be reached into a link.
 */
export function BreadcrumbTrail({
  crumbs,
}: {
  readonly crumbs: readonly Breadcrumb[];
}): JSX.Element {
  return (
    <>
      {crumbs.map((crumb, index) => (
        <Fragment key={`${String(index)}-${crumb.label}`}>
          {index > 0 ? (
            <span
              aria-hidden="true"
              className="evido-file-listing__crumb-separator"
            >
              {BREADCRUMB_SEPARATOR}
            </span>
          ) : null}
          {crumb.onNavigate ? (
            <Button
              className="evido-file-listing__crumb"
              onClick={crumb.onNavigate}
              variation="link"
            >
              {crumb.label}
            </Button>
          ) : (
            <span>{crumb.label}</span>
          )}
        </Fragment>
      ))}
    </>
  );
}

type ToolbarActionStyle = "icon" | "labelled";

type ToolbarAction = {
  readonly actionType: string;
  readonly iconName: ToolbarIconName;
  readonly label: string;
  readonly style: ToolbarActionStyle;
  /** Actions that only apply to some folders, or to some roles. */
  readonly isAvailable?: (
    location: LocationBreadcrumbSource,
    role: AppRole,
  ) => boolean;
};

type ResolvedToolbarAction = ToolbarAction & { readonly isDisabled: boolean };

/** The part of a Storage Browser action list entry the toolbar reads. */
type ActionListEntry = {
  readonly actionType: string;
  readonly isDisabled?: boolean;
  readonly isHidden?: boolean;
};

/**
 * Whether the viewer may add files here.
 */
function isUploadAvailable(
  location: LocationBreadcrumbSource,
  role: AppRole,
): boolean {
  if (!location.current?.permissions.includes("write")) {
    return false;
  }
  return role === AppRole.Customer || isStaffWritableFolder(location, role);
}

const TOOLBAR_ACTIONS: readonly ToolbarAction[] = [
  {
    actionType: DOWNLOAD_ACTION_TYPE,
    iconName: "download",
    label: "Download",
    style: "icon",
  },
  {
    actionType: UPLOAD_ACTION_TYPE,
    iconName: "upload-file",
    label: "Upload",
    style: "icon",
    isAvailable: isUploadAvailable,
  },
  {
    actionType: DELETE_ACTION_TYPE,
    iconName: "delete-file",
    label: "Delete",
    style: "icon",
    isAvailable: isDeleteAvailable,
  },
  {
    actionType: APPROVE_ACTION_TYPE,
    iconName: "file-text",
    label: "Approve",
    style: "labelled",
    isAvailable: isReviewAvailable,
  },
  {
    actionType: REJECT_ACTION_TYPE,
    iconName: "delete-file",
    label: "Reject",
    style: "labelled",
    isAvailable: isReviewAvailable,
  },
];

function resolveToolbarAction(
  action: ToolbarAction,
  actionItems: readonly ActionListEntry[],
  location: LocationBreadcrumbSource,
  role: AppRole,
): ResolvedToolbarAction | undefined {
  const item = actionItems.find(
    (candidate) => candidate.actionType === action.actionType,
  );
  if (!item || item.isHidden) {
    return undefined;
  }
  if (action.isAvailable && !action.isAvailable(location, role)) {
    return undefined;
  }
  return { ...action, isDisabled: item.isDisabled ?? false };
}

/**
 * Build the replacement for the Storage Browser's own location detail view.
 *
 * The built-in view heads the page with the bucket path and the raw S3 prefix,
 * which the approved design replaces with a breadcrumb of product folder names.
 * Its table is kept as-is: it already carries the four approved columns, row
 * selection and the per-row download, so only the chrome around it is rebuilt.
 */
export function createFileListingView(
  useView: UseView,
  LocationDetailView: StorageBrowserType["LocationDetailView"],
  role: AppRole,
  /** Tells the browser's file validation which rules the browsed folder applies. */
  setUploadRules: (rules: UploadRules) => void,
  projectPrefixRoot: string,
  /** Set only for a customer with several projects, to lead back to their project list. */
  projectSelection?: ProjectSelection,
): () => JSX.Element {
  function FileListingView(): JSX.Element {
    const detailState = useView("LocationDetail");
    const navigateToProjects = projectSelection
      ? () => {
          projectSelection.select(undefined);
          detailState.onNavigateHome();
        }
      : undefined;
    useSearchAsTyped(
      detailState.searchQuery,
      detailState.location.key,
      detailState.onSearch,
      detailState.onSearchClear,
    );
    const breadcrumbs = buildBreadcrumbs(
      detailState.location,
      detailState.onNavigateHome,
      detailState.onNavigate,
      projectPrefixRoot,
      navigateToProjects,
    );
    const hasNoRows =
      !detailState.isLoading &&
      !detailState.hasError &&
      detailState.pageItems.length === 0;
    const canUpload = isUploadAvailable(detailState.location, role);
    const isFolderTruncated =
      detailState.hasNextPage && detailState.searchQuery === "";
    /** An empty folder the viewer may add to invites the upload, rather than explain. */
    const showDropZone =
      hasNoRows && detailState.searchQuery === "" && canUpload;
    const emptyState =
      hasNoRows && !showDropZone
        ? emptyStateContent(
            detailState.location,
            detailState.searchQuery,
            detailState.hasExhaustedSearch,
          )
        : undefined;
    const uploadRules = uploadRulesFor(detailState.location);
    useEffect(() => {
      setUploadRules(uploadRules);
    }, [uploadRules, setUploadRules]);
    const [rejectedFilesMessage, setRejectedFilesMessage] = useState<
      string | undefined
    >();
    useEffect(() => {
      setRejectedFilesMessage(undefined);
    }, [detailState.location.key]);

    /** Only enter the upload view when the drop holds something the folder takes. */
    function handleDroppedFiles(files: File[]): void {
      const acceptedFiles = files.filter((file) =>
        isAcceptableUploadFile(file, uploadRules),
      );
      if (acceptedFiles.length > 0) {
        setRejectedFilesMessage(undefined);
        detailState.onDropFiles(files);
        return;
      }
      setRejectedFilesMessage(
        rejectedFilesSummary(
          files.map((file) => ({ file })),
          uploadRules,
        ),
      );
    }

    const { openFilePicker, filePickerInput } = useUploadFilePicker(
      handleDroppedFiles,
      uploadAcceptAttribute(uploadRules),
    );

    function selectAction(actionType: string): void {
      if (actionType === UPLOAD_ACTION_TYPE) {
        openFilePicker();
        return;
      }
      detailState.onActionSelect(actionType);
    }

    const toolbarControls = TOOLBAR_ACTIONS.map((action) =>
      resolveToolbarAction(
        action,
        detailState.actionItems,
        detailState.location,
        role,
      ),
    )
      .filter((action) => action !== undefined)
      .map((action) => {
        const ActionButton =
          action.style === "labelled"
            ? ToolbarLabelledButton
            : ToolbarIconButton;
        return (
          <ActionButton
            iconName={action.iconName}
            isDisabled={action.isDisabled}
            key={action.actionType}
            label={action.label}
            onSelect={() => {
              selectAction(action.actionType);
            }}
          />
        );
      });

    const uploadPrompt = (
      <UploadDropZone
        formatsLabel={uploadLimitsLabel(uploadRules)}
        onBrowse={openFilePicker}
      />
    );
    const emptyStateCard = emptyState ? (
      <EmptyStateCard content={emptyState} />
    ) : null;

    const panelContent = (
      <>
        <LocationDetailView.LocationItemsTable />
        {showDropZone ? uploadPrompt : null}
        {emptyStateCard}
      </>
    );

    const droppablePanel = (
      <LocationDetailView.DropZone>{panelContent}</LocationDetailView.DropZone>
    );

    const readOnlyPanel = (
      <Flex
        className="evido-listing__content"
        direction="column"
        flex="1"
        gap="0"
        minHeight="0"
      >
        {panelContent}
      </Flex>
    );

    return (
      <LocationDetailView.Provider
        {...detailState}
        onActionSelect={selectAction}
        onDropFiles={handleDroppedFiles}
      >
        <ListingLayout
          controls={
            <>
              <LocationDetailView.Refresh />
              {toolbarControls}
            </>
          }
          message={
            <Flex direction="column" gap="0.5rem">
              <LocationDetailView.Message />
              {isFolderTruncated ? (
                <Message colorTheme="warning">
                  {TRUNCATED_FOLDER_MESSAGE}
                </Message>
              ) : null}
              {rejectedFilesMessage ? (
                <Message colorTheme="error">{rejectedFilesMessage}</Message>
              ) : null}
            </Flex>
          }
          search={<LocationDetailView.Search />}
          title={<BreadcrumbTrail crumbs={breadcrumbs} />}
        >
          {filePickerInput}
          {detailState.hasError ? null : (
            <Flex direction="row" flex="1" gap="1rem" minHeight="0">
              <Flex
                borderColor="border.primary"
                borderRadius="large"
                borderStyle="solid"
                borderWidth="1px"
                className="evido-listing__panel evido-file-listing__panel"
                direction="column"
                flex="1"
                gap="0"
                minHeight="0"
                minWidth="0"
                overflow="auto"
              >
                {canUpload ? droppablePanel : readOnlyPanel}
              </Flex>
              {/* Opens beside the table, as in the browser's own layout. */}
              <LocationDetailView.FilePreview />
            </Flex>
          )}
        </ListingLayout>
      </LocationDetailView.Provider>
    );
  }

  return FileListingView;
}
